import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GET_COMPONENTS_CREATE_REQUESTS } from '@api/queries/components';
import { GET_USERS } from '@api/queries/users';
import { GET_COMPONENT_VENDORS_BY_IDS } from '@api/queries/vendor';
import { type SimpleUser } from '@models/SimpleUser';
import { flatMapEdges } from '@utils/flatMapEdges';
import { type ComponentCreateRequestMapped } from '@pages/notification-components/models/ComponentCreateRequestMapped';

export interface UseNewComponentRequests {
    requests: ComponentCreateRequestMapped[];
}

export function useNewComponentRequests(): UseNewComponentRequests {
    const getUser = (userId: string): SimpleUser => {
        return (
            users.find((user) => user.id === userId) || {
                id: userId,
                firstName: 'Unknown',
                lastName: 'User',
            }
        );
    };

    const { data: requestsData } = useQuery(GET_COMPONENTS_CREATE_REQUESTS);
    const requestsNodes = flatMapEdges(
        requestsData?.componentCreateRequestsView,
    );

    const userIds = requestsNodes.map((node) => node.userID).filter(Boolean);
    const { data: usersData } = useQuery(GET_USERS, {
        variables: {
            where: {
                idIn: userIds,
            },
        },
        skip: userIds.length === 0,
    });

    const componentVendorIds = requestsNodes
        .map((node) => node.componentVendorID)
        .filter((id): id is string => Boolean(id));
    const { data: vendorsData } = useQuery(GET_COMPONENT_VENDORS_BY_IDS, {
        variables: {
            ids: componentVendorIds,
        },
        skip: componentVendorIds.length === 0,
    });

    const users =
        flatMapEdges(usersData?.users).map((u) => ({
            id: u.id,
            firstName: u.firstName,
            lastName: u.lastName,
        })) || [];

    const requests: ComponentCreateRequestMapped[] = useMemo(
        () =>
            requestsNodes.map((node) => ({
                ...node,
                user: getUser(node.userID),
                comments: node.comments.map((comment) => ({
                    content: comment.comment,
                    createTime: comment.createTime,
                    user: getUser(comment.userID),
                })),
                componentVendor: vendorsData?.componentVendors.edges.find(
                    (edge) => edge.node.id === node.componentVendorID,
                )?.node,
            })) || [],
        [requestsNodes, users, vendorsData],
    );

    return {
        requests,
    };
}
